import * as React from 'react';
import Box from '@mui/material/Box';
import {
    Card,
    Stepper as MuiStepper,
    Typography,
    CircularProgress,
    CardHeader,
    IconButton,
    useTheme,
    Tooltip,
} from '@mui/material';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import {StepPage} from './partials/StepPage';
import {useEffect} from 'react';
import {Check, Delete, Undo} from '@mui/icons-material';
import {styled} from '@mui/system';

interface StepperProps {
    steps: Array<any>;
    form: any;
    onSubmit: any;
    isUpdate?: any;
    setSchema?: any;
    loading?: boolean;
    title?: string;
}

const StepperLoader = styled(Box)(({theme}) => ({
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 0,
    top: 0,
    zIndex: 9999,
    borderRadius: 4,
    display: 'flex',
    filter: 'blur(0) !important',
    backdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(255,255,255,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
}));

const Stepper = (props: StepperProps) => {
    const {steps = [], form, onSubmit, isUpdate = false, setSchema, loading, title} = props;

    const [activeStep, setActiveStep] = React.useState(0);
    const [completed, setCompleted] = React.useState<{
        [k: number]: boolean;
    }>({});
    const [error, setError] = React.useState<any>(null);
    const [allCompleted, setAllCompleted] = React.useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (isUpdate) {
            const newCompleted = {} as any;

            Array.from({length: steps.length}).forEach((_, index: number) => {
                newCompleted[index] = true;
            });
            setCompleted(newCompleted);
            setAllCompleted(true);
        }
    }, [isUpdate]);

    useEffect(() => {
        setSchema(steps[activeStep]?.schema);
    }, [activeStep]);

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted() ? steps.findIndex((step, i) => !(i in completed)) : activeStep + 1;
        setActiveStep(newActiveStep);
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };

    const handleComplete = async () => {
        const res = await validator(steps[activeStep]?.schema);

        if (res.isValid) {
            const newCompleted = completed;
            newCompleted[activeStep] = true;
            setCompleted(newCompleted);
            if (steps.length !== Object.keys(completed).length) {
                handleNext();
            } else {
                setAllCompleted(true);
            }
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setCompleted({});
    };

    const validator = async (schema: any) => {
        var isValid = true;
        var message: any = '';

        const tab = steps[activeStep];
        const type = steps[activeStep]?.type;

        if (type === 'element') {
            if (!tab.validation) {
                isValid = false;
                message = tab.validationMessage;

                setError({
                    isValid: false,
                    message: tab.validationMessage,
                });

                setTimeout(() => {
                    setError(null);
                }, 3000);
            }
        }

        return {
            isValid,
            message,
        };
    };

    return (
        <Box sx={{width: '100%', overflow: 'hidden', position: 'relative', pb: 4}}>
            {loading && (
                <StepperLoader>
                    <CircularProgress />
                </StepperLoader>
            )}
            <Card sx={{p: 3, pt: 1, mb: 4, height: 200}}>
                <Box
                    sx={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', mb: 2}}
                >
                    <Typography variant="h4">{title}</Typography>
                    <Tooltip title="Undo Completed Steps">
                        <IconButton size="large" onClick={handleReset}>
                            <Undo fontSize="large" sx={{color: theme.palette.primary.main}} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <MuiStepper sx={{height: 40}} nonLinear activeStep={activeStep}>
                    {completed &&
                        steps.map((step, index) => (
                            <Step key={step.label} completed={completed[index]}>
                                <StepButton
                                    icon={
                                        <Box
                                            sx={{
                                                width: activeStep === index ? 35 : 25,
                                                height: activeStep === index ? 35 : 25,
                                                borderRadius: '100%',
                                                bgcolor: theme.palette.primary.main,
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: activeStep === index ? 18 : 14,
                                                transition: '.2s ease all',
                                            }}
                                        >
                                            {completed[index] ? (
                                                <Check fontSize={activeStep === index ? 'large' : 'medium'} />
                                            ) : (
                                                index + 1
                                            )}
                                        </Box>
                                    }
                                    color="inherit"
                                    onClick={handleStep(index)}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: activeStep === index ? 'bold' : 'normal',
                                            fontSize: activeStep === index ? 18 : 14,
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                </StepButton>
                            </Step>
                        ))}
                </MuiStepper>
                <Box sx={{display: 'flex', flexDirection: 'row', mt: 3}}>
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{mr: 1}}
                    >
                        Back
                    </Button>
                    <Box sx={{flex: '1 1 auto'}} />
                    <Button variant="outlined" onClick={handleNext} sx={{mr: 1}}>
                        Next
                    </Button>
                    {
                        <Button
                            variant="contained"
                            onClick={allCompleted ? form.handleSubmit(onSubmit) : form.handleSubmit(handleComplete)}
                        >
                            {totalSteps() === completedSteps() ? (isUpdate ? 'Update' : 'Finish') : 'Complete Step'}
                        </Button>
                    }
                    <>{allCompleted}</>
                </Box>
            </Card>
            <div>
                <React.Fragment>
                    <Card>
                        <StepPage tab={steps[activeStep]} form={form} error={error} />
                    </Card>
                </React.Fragment>
            </div>
        </Box>
    );
};

export default Stepper;
